import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { formatDateOrDateRange } from 'features/common/datetimeUtils';
import Highcharts from 'highcharts';

import { binEndFromTs } from '../common/slotsUtils';
import { HH_MM_FORMAT } from '../common/utils';
import { isComparisonMonth } from '../task_bar/ComparisonPicker';
import { ChartVariant } from './chartOptions';
import {
  axis_color,
  day_grid_color,
  font_size,
  freeflow_color,
  getBandMembership,
  graph_highlight_color,
  grid_color,
  label_color,
} from './chartOptions';

dayjs.extend(customParseFormat);
dayjs.extend(advancedFormat);

const TICK_INTERVAL_HOUR = 1000 * 60 * 60; // milliseconds * seconds * minutes

// highcharts-react npm page

export function getFormattedTime(ts): string {
  return dayjs(ts).format(HH_MM_FORMAT);
}

export function formatTargetSeriesName(
  targetDate: string,
  generic?: boolean,
  endDate?: string,
): string {
  if (generic) return 'Selected date';
  return formatDateOrDateRange(targetDate, endDate, 'ddd D MMM');
}

export function getDowWord(
  dow: number,
  uses_middle_east_day_types: boolean,
  longform: boolean = false,
): string {
  let dowWord = 'Weekdays';
  if (uses_middle_east_day_types) {
    if (dow === 5) {
      dowWord = longform ? 'Fridays' : 'Fris';
    } else if (dow === 6) {
      dowWord = longform ? 'Saturdays' : 'Sats';
    }
  } else {
    // eslint-disable-next-line no-lonely-if
    if (dow === 0) {
      dowWord = longform ? 'Sundays' : 'Suns';
    } else if (dow === 6) {
      dowWord = longform ? 'Saturdays' : 'Sats';
    }
  }
  return dowWord;
}

export function formatComparisonMonthName(
  comparison,
  target,
  uses_middle_east_day_types,
  full_daytype = false,
): string {
  const dow = dayjs(target).day();
  const dowWord = getDowWord(dow, uses_middle_east_day_types, full_daytype);
  return `${dowWord} ${dayjs(comparison, 'MMMM-YYYY').format("MMM 'YY")}`;
}

export const formatTypicalSeriesName = (
  comparison: string,
  targetDate: string,
  uses_middle_east_day_types: boolean,
  generic?: boolean,
  current_ts?: number,
  full_daytype: boolean = false,
  comparisonEndDate?: string,
) => {
  if (!comparison) {
    return '';
  }
  if (generic) return 'Typical';
  if (isComparisonMonth(comparison)) {
    return formatComparisonMonthName(
      comparison,
      current_ts || targetDate,
      uses_middle_east_day_types,
      full_daytype,
    );
  }

  // Assume the comparison is a day, if current_ts is present, display the offset from current_ts (dependent on hover)
  if (current_ts) {
    const offset = dayjs(comparison).diff(dayjs(targetDate));
    const comparisonDisplayDate = dayjs(current_ts).add(offset);
    return formatTargetSeriesName(
      comparisonDisplayDate.format('YYYY-MM-DD'),
      false,
      comparisonEndDate,
    );
  }

  // Static display
  return formatTargetSeriesName(comparison, false, comparisonEndDate);
};

export enum SeriesGroupType {
  Nrt = 'Nrt',
  Comparison = 'Comparison',
  Other = 'Other',
}

export type SeriesGroups = {
  [key in SeriesGroupType]?: string[];
};

function getSeriesNameForGroup(
  seriesGroup: SeriesGroupType,
  ts,
  targetDate,
  comparison,
  fallback_series_name,
  uses_middle_east_day_types,
  isEstimated = false,
  targetEndDate?: string,
  comparisonEndDate?: string,
) {
  let prefix = '';
  if (isEstimated) {
    prefix = 'Estimated ';
  }
  if (seriesGroup === SeriesGroupType.Nrt) {
    return (
      prefix +
      formatTypicalSeriesName(
        targetDate,
        targetDate,
        uses_middle_east_day_types,
        false,
        ts,
        false,
        targetEndDate,
      )
    );
  } else if (seriesGroup === SeriesGroupType.Comparison) {
    return (
      prefix +
      formatTypicalSeriesName(
        comparison,
        targetDate,
        uses_middle_east_day_types,
        false,
        ts,
        false,
        comparisonEndDate,
      )
    );
  }

  return prefix + fallback_series_name;
}

export const makeFormatTooltip = (
  bands,
  bin_size,
  uses_middle_east_day_types: boolean,
  value_formatter,
  targetDate: string,
  comparison: string,
  showSeriesGroups: SeriesGroups, // Groups of series Ids, the tooltip will show the first in each group
  freeflow_val: number | undefined,
  variant?: ChartVariant,
  targetEndDate?: string,
  comparisonEndDate?: string,
) => {
  function formatTooltip(
    this: Highcharts.TooltipFormatterContextObject,
    tooltip: Highcharts.Tooltip,
  ) {
    const points_by_series_id = {};
    let chart_series;
    let ts;
    for (let i = 0; i < this.points.length; i++) {
      points_by_series_id[this.points[i].series.userOptions.id] =
        this.points[i];
      chart_series = this.points[i].series.chart.series;
      ts = ts || (this.points[i].x as number);
    }
    const series_by_id = {};
    chart_series = chart_series || [];
    for (let i = 0; i < chart_series.length; i++) {
      series_by_id[chart_series[i].userOptions.id] = chart_series[i];
    }

    const start_time = getFormattedTime(ts);
    const band = getBandMembership(ts, bands);
    const end_time = getFormattedTime(binEndFromTs(ts, bin_size));
    const isRmVariant = variant === ChartVariant.RouteMonitoring;
    let output = `
        <div class="chart-legend" style="font-size: ${isRmVariant ? '12px' : '15px'}">
            <h1 style="font-size: ${isRmVariant ? '12px' : '15px'}">${start_time} - ${end_time}</h1>
            <table>`;
    if (band) {
      // console.log(band);
      // Justifying FF0E: https://kschaul.com/til/2022/09/13/coloring-emoji-unicode-characters-on-ios-safari/
      output += `
      <tr>
        <td>
            <span class="highlight-icon" style="color:${graph_highlight_color}">◼&#xFE0E;</span>Highlight
        </td>
        <td>
            <b>${getFormattedTime(band.raw_from)} - ${getFormattedTime(
              band.raw_to,
            )}</b>
        </td>
      </tr>`;
    }
    const showSeriesGroupsKeys: SeriesGroupType[] = Object.keys(
      showSeriesGroups,
    ) as any;
    for (let i = 0; i < showSeriesGroupsKeys.length; i++) {
      let foundForGroup = false;
      const seriesGroupKey = showSeriesGroupsKeys[i];
      const seriesGroup = showSeriesGroups[seriesGroupKey];

      for (let j = 0; j < seriesGroup.length; j++) {
        const point = points_by_series_id[seriesGroup[j]];
        if (point && point.x === ts) {
          foundForGroup = true;
          const series_name = getSeriesNameForGroup(
            showSeriesGroupsKeys[i],
            ts,
            targetDate,
            comparison,
            series_by_id[showSeriesGroups[seriesGroupKey][0]].name,
            uses_middle_east_day_types,
            seriesGroup[j].includes('questionable_'),
            targetEndDate,
            comparisonEndDate,
          );
          output += `
          <tr>
              <td style="color: ${isRmVariant ? 'rgba(255, 255, 255, 0.70)' : '#FFF'}">
                  <span style="color:${point.color}">●</span>${series_name}
              </td>
              <td>
                  <b>${value_formatter(point.y)}</b>
              </td>
          </tr>`;
          break;
        }
      }
      if (!foundForGroup) {
        const series_name = getSeriesNameForGroup(
          showSeriesGroupsKeys[i],
          ts,
          targetDate,
          comparison,
          series_by_id[showSeriesGroups[seriesGroupKey][0]].name,
          uses_middle_east_day_types,
          false,
          targetEndDate,
          comparisonEndDate,
        );
        output += `
          <tr>
              <td>
                  <span style="color:${
                    series_by_id[seriesGroup[0]].color
                  }">●</span>${series_name}
              </td>
              <td>
                  <b>No Sample</b>
              </td>
          </tr>`;
      }
    }
    if (freeflow_val) {
      output += `
        <tr>
            <td style="color: ${isRmVariant ? 'rgba(255, 255, 255, 0.70)' : '#FFF'}">
                <span style="color:${freeflow_color}">●</span>Freeflow
            </td>

            <td>
                <b>${value_formatter(freeflow_val)}</b>
            </td>
        </tr>`;
    }
    return `${output}</table></div>`;
  }
  return formatTooltip;
};

export const nrtXAxisOptions = {
  type: 'datetime',
  dateTimeLabelFormats: {
    day: '%H:%M',
  },
  minPadding: 0,
  maxPadding: 0,
  // startOnTick: true,
  endOnTick: true,
  lineColor: axis_color,
  gridLineColor: grid_color, // vertical lines
  minorGridLineColor: grid_color,
  gridLineWidth: 1,
  tickPixelInterval: 50,
  tickInterval: TICK_INTERVAL_HOUR * 3,
  minorTickInterval: TICK_INTERVAL_HOUR,
  labels: {
    style: {
      color: label_color,
      fontSize: font_size,
    },
  },
  title: {
    text: 'Time of day',
    style: {
      color: label_color,
    },
  },
};

export const dayMarkerLineBase = {
  color: day_grid_color,
  width: 3,
};
